/** The upgrade section of Lux & billing (pure): what a plan adds over the company's current one, the annual saving
 * and the cost of a Lux on a plan against buying packs. Every number comes from the plans API; nothing here is a
 * price list. */
import type { MessageKey } from "../i18n/keys.ts";
import { fmtLux, fmtMoneyShort, type LuxWords } from "./lux.ts";

type Values = Record<string, unknown>;

function usd(c: number): string {
  return `$${(c / 100).toLocaleString("en-US", { minimumFractionDigits: c % 100 ? 2 : 0, maximumFractionDigits: 2 })}`;
}

function n(v: number): string {
  return v.toLocaleString("en-US");
}

type Rule = {
  key: string;
  /** "count": more (or unlimited) is better; "price": lower is better; "off": false is better; "priority": lower. */
  better: "count" | "price" | "off" | "priority";
  text: (v: number | boolean | null) => string;
  was?: (v: number | boolean | null) => string;
  /** The `lux` catalog's message (`value`: the formatted value; `unlimited`: "yes" when null). */
  msg: MessageKey;
  /** How the value goes into the message: a Lux amount, a price or a count. */
  as?: "lux" | "price" | "count";
};

/** The levers a customer notices, in the order the cards list them. */
const RULES: readonly Rule[] = [
  { key: "lux_month", msg: "lux.upgrade.hl.luxMonth", as: "lux", better: "count", text: (v) => `${fmtLux(v as number)} Lux a month`, was: (v) => fmtLux(v as number) },
  { key: "lux_reserved_designs", msg: "lux.upgrade.hl.luxReserved", as: "lux", better: "count", text: (v) => `of which ${fmtLux(v as number)} for automatic designs`, was: (v) => fmtLux(v as number) },
  { key: "seats", msg: "lux.upgrade.hl.seats", as: "count", better: "count", text: (v) => (v == null ? "Users by contract" : `${n(v as number)} users included`), was: (v) => `${n(v as number)}` },
  { key: "lux_price_cents", msg: "lux.upgrade.hl.luxPrice", as: "price", better: "price", text: (v) => `Extra Lux at ${usd(v as number)}`, was: (v) => usd(v as number) },
  { key: "websites", msg: "lux.upgrade.hl.websites", as: "count", better: "count", text: (v) => (v == null ? "Unlimited websites" : `${n(v as number)} websites with the widget`), was: (v) => `${n(v as number)}` },
  {
    key: "service_area_countries", msg: "lux.upgrade.hl.countries", as: "count",
    better: "count",
    text: (v) => (v == null ? "Design in every country" : `Design in ${n(v as number)} countries`),
    was: (v) => `${n(v as number)}`,
  },
  { key: "powered_by_badge", msg: "lux.upgrade.hl.badge", better: "off", text: () => "Your brand only: no “Powered by Solar OS” badge" },
  { key: "queue_priority", msg: "lux.upgrade.hl.priority", better: "priority", text: () => "Priority in the design queue" },
  { key: "audit_log_days", msg: "lux.upgrade.hl.auditLog", as: "count", better: "count", text: (v) => `${n(v as number)} days of audit log and email history`, was: (v) => `${n(v as number)} days` },
  { key: "webhook_endpoints", msg: "lux.upgrade.hl.webhooks", as: "count", better: "count", text: (v) => `${n(v as number)} webhook endpoints`, was: (v) => `${n(v as number)}` },
  { key: "utility_prices", msg: "lux.upgrade.hl.utilityPrices", as: "count", better: "count", text: (v) => (v == null ? "Unlimited utility prices" : `${n(v as number)} company utility prices`), was: (v) => `${n(v as number)}` },
];

function scalar(v: unknown): number | boolean | null | undefined {
  if (v === null) return null;
  if (typeof v === "number" || typeof v === "boolean") return v;
  return undefined; // missing, or a per-verification-tier value: not compared
}

function isBetter(rule: Rule, cur: number | boolean | null, tgt: number | boolean | null): boolean {
  switch (rule.better) {
    case "count":
      if (tgt === null) return cur !== null;
      return typeof tgt === "number" && typeof cur === "number" && tgt > cur;
    case "price":
    case "priority":
      return typeof tgt === "number" && typeof cur === "number" && tgt < cur;
    case "off":
      return tgt === false && cur === true;
  }
}

export type Highlight = { key: string; text: string; was: string | null };

/** A rule's value as its message shows it, in the reader's locale. */
function wordsValue(rule: Rule, v: number | boolean | null, w: LuxWords): string {
  if (typeof v !== "number") return "";
  if (rule.as === "lux") return fmtLux(v, w.locale);
  if (rule.as === "price") return fmtMoneyShort(v, "USD", w.locale);
  return v.toLocaleString(w.locale);
}

/** What `target` gives that `current` does not, best first (in the reader's language with `w`). */
export function planHighlights(current: Values, target: Values, w?: LuxWords): Highlight[] {
  const out: Highlight[] = [];
  for (const rule of RULES) {
    const cur = scalar(current[rule.key]);
    const tgt = scalar(target[rule.key]);
    if (cur === undefined || tgt === undefined || !isBetter(rule, cur, tgt)) continue;
    if (w) {
      const was = rule.was && cur !== null ? (rule.key === "audit_log_days" ? w.t("lux.planValue.days", { count: cur as number }) : wordsValue(rule, cur, w)) : null;
      out.push({ key: rule.key, text: w.t(rule.msg, { value: wordsValue(rule, tgt, w), count: typeof tgt === "number" ? tgt : 0, unlimited: tgt === null ? "yes" : "no" }), was });
      continue;
    }
    out.push({ key: rule.key, text: rule.text(tgt), was: rule.was && cur !== null ? rule.was(cur) : null });
  }
  return out;
}

/** Whole percent saved by annual billing (rounded down, so never overstated). */
export function annualSavingPct(monthlyCents: number, annualMonthlyCents: number): number {
  if (!(monthlyCents > 0) || annualMonthlyCents >= monthlyCents) return 0;
  return Math.floor(((monthlyCents - annualMonthlyCents) / monthlyCents) * 100);
}

/** A plan's cost per Lux when the month's Lux is used (cents, rounded down; `luxPerMonth` in hundredths). */
export function planCentsPerLux(monthlyCents: number, luxPerMonth: number): number | null {
  return monthlyCents > 0 && luxPerMonth > 0 ? Math.floor((monthlyCents * 100) / luxPerMonth) : null;
}

export type PlanVsPacks = { lux: number; planCents: number; perLuxCents: number; packPerLuxCents: number; packCents: number; savingCents: number };

/** The plan against buying the same Lux in packs each month; null unless the plan is truly cheaper. */
export function planVsPacks(monthlyCents: number, luxPerMonth: number, packPerLuxCents: number | null): PlanVsPacks | null {
  const per = planCentsPerLux(monthlyCents, luxPerMonth);
  if (per == null || packPerLuxCents == null) return null;
  const packCents = Math.floor((packPerLuxCents * luxPerMonth) / 100);
  if (packCents <= monthlyCents) return null;
  return { lux: luxPerMonth, planCents: monthlyCents, perLuxCents: per, packPerLuxCents, packCents, savingCents: packCents - monthlyCents };
}

/** A price after a percent-off offer (cents, rounded). */
export function afterPercentOff(cents: number, pct: number | null | undefined): number {
  return pct ? Math.round(cents * (1 - pct / 100)) : cents;
}
